import BaseComponent from '@/components/base';
import { defaultColor, colorMap, hairTypeColors } from '@/utils/colors-map';

export default class ColorSelection extends BaseComponent {
  static get observedAttributes() {
    return [
      'colors',
      'excluded-colors',
      'product',
      'preselected-color',
      'hide-fragrance-text',
      'hair-type',
      'colorOptionsJsonFormatted'
    ];
  }

  static get hairTypeColors() {
    return hairTypeColors;
  }

  onConstruct() {
    this.selectedColor = null;
    this.onSelected = () => {};
    this.domReady = false;
  }

  get preselectedColor() {
    return this.getAttribute('preselected-color') || defaultColor;
  }

  get hideFragranceText() {
    return this.getAttribute('hide-fragrance-text') === '';
  }

  render() {
    this.excludedColors = (this.getAttribute('excluded-colors') || '').split(
      ','
    );
    const productType = this.product.includes('conditioner')
      ? 'conditioner'
      : 'default';
    const productColors = colorMap[productType];
    return `

        <style>
            .selected {
                border-color: black !important;
            }
            .hide {
              display: none;
            }
            .visible {
              display: block;
            }
        </style>
      <div id="swatchesColorsA" style="display: flex; overflow-x: auto; gap: 10px; padding: 10px">
      ${this.colors
        .filter((color) => !this.excludedColors.includes(color.toLowerCase()))
        .map(
          (color, index) => `
        <div style="display: flex; align-items: center; gap: 5px;cursor: pointer;">
          <div
              class="color-option"
              value="${color}"
              style="
                  width: 36px;
                  height: 36px;
                  border-radius: 36px;
                  background-color: ${
                    this.colorsOptions[index] || productColors[color]
                  };
                  flex-shrink: 0;
                  border: 2px solid #e0e0e0;
                  "
              >
          </div>
          <span id="color-${color
            .toLowerCase()
            .replace(/ /g, '-')}" class="hide">${color}</span>
        </div>
        `
        )
        .join('')}
      </div>
    `;
  }

  afterMount() {
    this.initializeColorSelection();
  }

  initializeColorSelection() {
    this.shadowRoot.querySelectorAll('.color-option').forEach((colorOption) => {
      const colorValue = colorOption.getAttribute('value');
      if (colorValue === this.preselectedColor) {
        colorOption.classList.add('selected');
        this.selectedColor = colorValue;
      }

      if (!this.hideFragranceText) this.toggleFragranceText();

      if (this.selectedColor) {
        const colorTextElm = this.shadowRoot.querySelector(
          `#color-${this.selectedColor.toLocaleLowerCase().replace(/ /g, '-')}`
        );
        if (colorTextElm) {
          colorTextElm.classList.remove('hide');
        }
      }

      colorOption.addEventListener('click', () => {
        this.selectedColor = colorOption.getAttribute('value');
        this.shadowRoot
          .querySelector('.selected')
          ?.classList?.remove('selected');
        colorOption.classList.add('selected');
        if (!this.hideFragranceText) this.toggleFragranceText();

        this.onSelected && this.onSelected(this.selectedColor);

        const allColorsOptions =
          this.shadowRoot.querySelectorAll('[id^="color-"]');
        allColorsOptions.forEach((colorOption) => {
          const colorValue = this.selectedColor
            .toLocaleLowerCase()
            .replace(' ', '-');
          if (`color-${colorValue}` === colorOption.id) {
            colorOption.classList.remove('hide');
          } else {
            colorOption.classList.add('hide');
          }
        });
      });
    });

    // Initialize colors based on hair type
    this.initHairTypeColors();

    // Mark as initialized
    this.domReady = true;
  }

  initHairTypeColors() {
    const hairType = this.hairType;
    const hairTypeColors = ColorSelection.hairTypeColors;
    const hairTypeColorsOptions = hairTypeColors[hairType];

    if (hairTypeColorsOptions && hairType) {
      const allColorOptions = this.shadowRoot.querySelectorAll('.color-option');
      let lastValidColorOption = null;

      allColorOptions.forEach((colorOption) => {
        const colorValue = colorOption.getAttribute('value');

        if (!hairTypeColorsOptions.includes(colorValue)) {
          // Hide the color option
          colorOption.style.display = 'none';
          // Hide the associated text span as well
          const colorTextElm = this.shadowRoot.querySelector(
            `#color-${colorValue.toLowerCase().replace(/ /g, '-')}`
          );
          if (colorTextElm) {
            colorTextElm.style.display = 'none';
          }
        } else {
          // Keep the option visible and mark as last valid
          colorOption.style.display = 'block';
          lastValidColorOption = colorOption;
        }
      });

      // Select the last valid color if none is selected
      if (lastValidColorOption && !this.selectedColor) {
        const colorValue = lastValidColorOption.getAttribute('value');
        this.selectedColor = colorValue;

        // Remove previous selection
        this.shadowRoot
          .querySelector('.selected')
          ?.classList?.remove('selected');

        // Select the new color
        lastValidColorOption.classList.add('selected');

        // Show the selected color text
        const colorTextElm = this.shadowRoot.querySelector(
          `#color-${colorValue.toLowerCase().replace(/ /g, '-')}`
        );
        if (colorTextElm) {
          colorTextElm.classList.remove('hide');
        }

        // Call callback if exists
        this.onSelected && this.onSelected(this.selectedColor);
      }
    }
  }

  toggleFragranceText() {
    const allText = this.shadowRoot.querySelectorAll('span');

    allText.forEach((span) => {
      const shouldHide = !span.id.startsWith(`color-${this.selectedColor}`);
      span.classList.toggle('hide', shouldHide);
    });
  }

  get colors() {
    const colors = JSON.parse(this.getAttribute('colors') || '[]');

    return colors;
  }

  get colorsOptions() {
    const colorsOptions = JSON.parse(
      this.getAttribute('colorOptionsJsonFormatted') || '[]'
    );

    return colorsOptions;
  }

  get product() {
    return this.getAttribute('product') || '';
  }

  get hairType() {
    const hairTypeValue = this.getAttribute('hair-type');
    return hairTypeValue || '';
  }
}
